import { AnalysisRun, AnalysisRunService } from "./analysis-run.service";
import { ANALYSIS_TIMEOUTS, AnalysisError, bounded, officeCall } from "../utils/analysis-async";
import { EvaluationFlowsEnum } from "../models/shared.model";
import { Inject, Injectable, Injector } from "@angular/core";
import { TAGS, HOST_NAMES } from "../models/constants";
import { WaitService } from "./wait.service";
import { IEvaluationStepsConfig, LinksWithStyles } from "../models/shared.model";
import { HtmlUtils } from "../utils/html.utils";
import { IEvaluator } from "../interfaces/evaluator-interface";
import { Evaluator } from "../evaluators/evaluator";
import { environment } from "../../environments/environment";
import { firstValueFrom } from "rxjs";
import { MailUtils } from "../utils/mail.utils";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { StringUtils } from "../utils/string.utils";

@Injectable()
export class LinksService {
  private _linksWStyles: LinksWithStyles[] | null = [];
  private _rawEmailBody: any = null;
  private _emailBody: any = null;

  constructor(
    private injector: Injector,
    private runs: AnalysisRunService,
    @Inject(WaitService) private waitService: WaitService,
    @Inject(HttpClient) private http: HttpClient
  ) {
    this.waitService.resetData.subscribe((reset) => {
      if (reset) {
        this._linksWStyles = [];
        this._emailBody = null;
        this._rawEmailBody = null;
      }
    });
  }

  get rawEmailBody(): any {
    return this._rawEmailBody;
  }

  get emailBody(): any {
    return this._emailBody;
  }

  get linksWStyles(): LinksWithStyles[] | null {
    return this._linksWStyles;
  }

  public async linksFlow(enumValue: string, flow: IEvaluationStepsConfig, run: AnalysisRun = this.runs.current) {
    let linkWithStyles = await this.getLinks(run);
    run.assertActive();
    let temp: Array<IEvaluator> = [];

    if (linkWithStyles != null && linkWithStyles.length > 0)
      for (let i: number = 0; i < linkWithStyles.length; i++) {
        const link: HTMLAnchorElement = linkWithStyles[i].link!;

        if (this.ignoreLink(link))
          continue;

        const ev = new Evaluator(this.injector, enumValue, flow, undefined, link, linkWithStyles[i].style);

        temp.push(ev);
      }

    return temp;
  }

  async getEmailBodyFromGraphAPI(run: AnalysisRun = this.runs.current): Promise<ArrayBuffer> {
    run.assertActive();
    const host = Office.context.mailbox.diagnostics.hostName;
    const itemId = host === HOST_NAMES.OUTLOOK_IOS || host === HOST_NAMES.OUTLOOK_ANDROID ? run.item.itemId :
      Office.context.mailbox.convertToRestId(run.item.itemId, Office.MailboxEnums.RestVersion.v2_0);
    const baseUrl = environment.apiConfig.microsoft_graph_url_personal;
    let subscription: import('rxjs').Subscription;
    try {
      return await bounded(() => new Promise<ArrayBuffer>((resolve, reject) => {
        subscription = this.http.get(`${baseUrl}/messages/${itemId}/$value`, {
          headers: new HttpHeaders({ Accept: 'message/rfc822' }), responseType: 'arraybuffer'
        }).subscribe({ next: resolve, error: reject });
      }), ANALYSIS_TIMEOUTS.graph, 'Message body', run);
    } finally { subscription?.unsubscribe(); }
  }

  private async officeGetEmailBody(run: AnalysisRun): Promise<string> {
    run.assertActive();
    if (!run.item) throw new AnalysisError('unavailable', 'No message selected');
    return officeCall<string>(callback => run.item.body.getAsync(Office.CoercionType.Html, {}, callback),
      'Message body', run);
  }

  async getEmailBody(run: AnalysisRun = this.runs.current): Promise<void> {
    run.assertActive();
    let body: string | null = null;
    try { body = HtmlUtils.extractHtmlFromMime(await this.getEmailBodyFromGraphAPI(run)); }
    catch { run.assertActive(); }
    if (body == null) body = await this.officeGetEmailBody(run);
    run.assertActive();
    this._rawEmailBody = body;
    run.body = body;
    run.bodyAvailable = true;
  }

  async getLinks(run: AnalysisRun = this.runs.current): Promise<LinksWithStyles[]> {
    run.assertActive();
    if (!run.bodyAvailable) throw new AnalysisError('unavailable', 'Message body unavailable');
    if (this._linksWStyles?.length) return this._linksWStyles;
    const emailBody = HtmlUtils.markAnchorIsConditional(run.body);
    let doc: Document;
    let stylesAvailable = true;
    try { doc = await HtmlUtils.documentToIframeAsync(emailBody, run); }
    catch (error) {
      run.assertActive();
      stylesAvailable = false;
      run.issue(EvaluationFlowsEnum.LINKS, error, 'link-styles');
      doc = new DOMParser().parseFromString(emailBody, 'text/html');
    }
    try {
      run.assertActive();
      const links = this.getLinksArray(doc).map(link => {
        let style: CSSStyleDeclaration = null;
        if (stylesAvailable) {
          const computed = this.getLinkStyles(link);
          // Snapshot live computed styles before removing the temporary iframe.
          style = document.createElement('span').style;
          Array.from(computed).forEach(property => style.setProperty(property, computed.getPropertyValue(property)));
          link.setAttribute('data-analysis-cursor', doc.defaultView?.getComputedStyle(link).cursor || '');
        } else link.setAttribute('data-analysis-styles-unavailable', 'true');
        return { link, style };
      });
      run.assertActive();
      this._emailBody = emailBody;
      this._linksWStyles = links;
      return links;
    } finally { doc.defaultView?.frameElement?.remove(); }
  }

  getLinksArray(originalDoc: Document) {
    let docCopy = originalDoc.cloneNode(true) as Document;

    //Removes <style> tags from the doc., because sometimes they have font links that are being marked as links in the analysis
    docCopy.querySelectorAll(TAGS.STYLE).forEach(styleTag => styleTag.remove());

    const links = originalDoc?.getElementsByTagName(TAGS.LINK);
    const linkArray= Array.from(links)

    // Add missing text URLs if not placed as anchors
    const anchors = docCopy?.querySelectorAll(TAGS.LINK);
    // Loop through all anchors and remove them
    anchors.forEach(anchor => {
      anchor.remove();
    });

    const urlRegex = /(?:https?:\/\/(?:[A-Za-z0-9._~%!$&'()*+,;=:-]+@)?|www\.(?:[A-Za-z0-9._~%!$&'()*+,;=:-]+@)?)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+(?::\d{2,5})?(?:\/[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;%=]*)?/g;
    const matches = docCopy.body.textContent.match(urlRegex);
    if(matches) 
      for (let match of matches) {
        const newLink = document.createElement(TAGS.LINK);
        newLink.href = match;
        newLink.text = match;
        linkArray.push(newLink);
      }
    return linkArray;
  }

  /**
   * Some email bodies might have fonts applied to the link, So we check if the link have parent of <font>
   *     if match first parent -> we apply its styles.
   *     if no, check first child,
   *     otherwise use the link styles
   */
  getLinkStyles(link: HTMLAnchorElement) {
    const view = link.ownerDocument.defaultView || window;
    let style = view.getComputedStyle(link);

    const firstFontTagChild = Array.from(link.children)
      .find(child => child.tagName.toLowerCase() === "font");

    if(link.closest("font"))
    {
      style = view.getComputedStyle(link.closest("font"));
    }
    else if(firstFontTagChild){
      style = view.getComputedStyle(firstFontTagChild);
    }

    return style;
  }

  ifLinkIsDisplayedInText(link: HTMLAnchorElement) {
    let href = link.getAttribute('href')
      .toLocaleLowerCase()
      .replace(/^\/\/|^.*?:(\/\/)?/, "")
      .replace("www.", "");

    if (href.endsWith("/"))
      href = href.substring(0, href.length - 1);

    return link.text.toLocaleLowerCase().includes(href);
  }

  ignoreLink(link: HTMLAnchorElement) {
    const excludedLinks = ["tel:", "mailto:"];
    const linkHref = link.getAttribute('href');
    if (linkHref) {
      return excludedLinks.some(item => linkHref.includes(item)) || this.ifInnerLink(linkHref);
    }
    return linkHref == null || linkHref == '' || linkHref == "http://" || linkHref == "https://";
  }

  //Check if the links navigate inside the html, not an actual link to external page
  ifInnerLink(linkHref: string){
    return !(linkHref.startsWith('http://') || linkHref.startsWith('https://') || linkHref.startsWith('www'));
  }

  isLinkDeceptive(url: string){
    url = this.decodeURL(url);

    //This regex check if a url includes an '@' to navigate to a different url, it extracts the navigated to url
    let exactUrl = url.match(/^(?:https?:\/\/|www\.)[^\/?#]+@((?:https?:\/\/|www\.)?[^\/?#]+)/);

    if(exactUrl && exactUrl.length > 0){
      return exactUrl[1];
    }

    return null;
  }

  /** This method tries to decode the url several time in case of the url is encoded on multi layers
   * ex: https://google.com%2540amazon.com
   * if we used decodeURIComponent(url) once it will result in -> https://google.com%40amazon.com
   */
  decodeURL(input: string, max = 3): string {
    let res = input;
    for (let i = 0; i < max; i++) {
      try {
        const decoded = decodeURIComponent(res);
        if (decoded === res) break;
        res = decoded;
      } catch {
        break;
      }
    }
    return res;
  }
}
